/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState } from 'react';

import Image from 'next/image';
import { PaymentCancelWrapper } from '../Components/PaymentCancelWrapper';
import DesktopProgress from '../Components/DesktopProgress';
import Chip from '@/lib/Chip';
import Icon from '@/lib/Icon/Icon';
import { useGlobalStore } from '@/store';
import { trackUserExits, trackUserRetention } from '@/analytics/views/landing';
import { formatContactNumber } from '@/utils/helpers';
import { getUserLoggedInStatus } from '@/analytics/utils';

const Header = (): JSX.Element => {
  const { paymentDetails, activeView, setActiveView, user, userAccessToken } = useGlobalStore();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { amount, gateway_merchant } = paymentDetails.data ?? {};
  const paymentAmount = Number(amount);
  const currentView = activeView;
  const setView = setActiveView;

  const [showCancel, setShowCancel] = useState(false);

  const gatewayMerchantName = gateway_merchant?.name ?? '';
  const parsedMerchantName = gatewayMerchantName.slice(0, 24);

  const handleBack = (): void => {
    if (['LANDING', 'PAYMENTS', 'DIGILOCKER_SUCCESS', 'E_MANDATE_SUCCESS'].includes(currentView)) {
      // show alert for cane
      setShowCancel(true);
      // Track Exit popup render
      return;
    }

    if (['EMI_PLANS', 'SELFIE_KYC', 'EMPLOYER_DETAILS'].includes(currentView)) {
      setView('DETAILS');
    } else if (currentView === 'DETAILS') {
      setView('LANDING');
    } else {
      setView('SELFIE_KYC');
    }
  };

  const onPaymentModalClose = (): void => {
    setShowCancel(false);
    // Track Error popup close
    trackUserExits({
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      contact: formatContactNumber(user.contact || ''),
      user_type: getUserLoggedInStatus(userAccessToken, user.contact),
    });
  };

  const handleContinue = (): void => {
    setShowCancel(false);
    // Track user retention click
    trackUserRetention({
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      contact: formatContactNumber(user.contact || ''),
      user_type: getUserLoggedInStatus(userAccessToken, user.contact),
    });
  };

  return (
    <>
      <div
        data-testid="fc-header"
        className="bg-primary-intense p-4 d:pt-12 flex shrink-0 d:w-30 d:flex-col"
      >
        <div className="flex items-start d:w-full">
          <button data-testid="fc-back" onClick={handleBack}>
            <Icon height={20} className="mt-2 " name="back" />
          </button>
          <div className="ml-4">
            <div className="flex">
              <h3 className="font-tasa font-medium text-white-normal text-xl d:text-2xl">
                Instant EMI{' '}
              </h3>
              <Chip className="ml-3 hidden d:flex relative pl-4 checkoutV2:pl-2.5 rounded-full">
                <Image
                  className="absolute -left-2.5 checkoutV2:-left-[0.9rem]"
                  width={24}
                  height={27}
                  alt="search"
                  src="/trusted.svg"
                />
                <p className="text-white-normal text-xs checkoutV2:text-[9px]">
                  Powered by Razorpay
                </p>
              </Chip>
            </div>
            {!paymentDetails.loading && gateway_merchant ? (
              <p className="text-white-normal font-normal text-sm d:text-base mt-2">
                {parsedMerchantName}
                {gatewayMerchantName.length > 24 ? '...' : ''}
                &nbsp;
                <span className="font-medium text-white-subtle">| ₹{paymentAmount / 100}</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="hidden d:block">
          <DesktopProgress />
          <Image
            alt="illustration"
            className="hidden d:block absolute bottom-0 "
            src="/shopping.svg"
            width={300}
            height={400}
          />
        </div>
      </div>
      {showCancel ? (
        <PaymentCancelWrapper
          onDismiss={onPaymentModalClose}
          isOpen={showCancel}
          ctaText="Exit"
          title="Are you sure you want to exit?"
          description="You will be taken back to all EMI payment options"
        >
          <div
            aria-hidden
            data-testid="continue-btn"
            onClick={handleContinue}
            className="text-center text-primary-normal mt-5 cursor-pointer"
          >
            Continue
          </div>
        </PaymentCancelWrapper>
      ) : null}
    </>
  );
};

export default Header;
